import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Button, Typography, IconButton, Portal } from "@mui/material";
import {
  Close as CloseIcon,
  DragIndicator as DragIcon,
} from "@mui/icons-material";
import { Colors } from "../../../configs";
import { primaryAlpha } from "../../../configs/colors";

interface CalculatorProps {
  isOpen: boolean;
  onClose: () => void;
  onResult?: (result: number) => void;
}

const Calculator: React.FC<CalculatorProps> = ({ isOpen, onClose, onResult }) => {
  const [display, setDisplay] = useState("0");
  const [calculationString, setCalculationString] = useState("");
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const calculatorRef = useRef<HTMLDivElement>(null);
  const displayScrollRef = useRef<HTMLDivElement>(null);
  const calcScrollRef = useRef<HTMLDivElement>(null);

  const [pos, setPos] = useState<{ x: number | null; y: number | null }>({
    x: null,
    y: null,
  });

  type DragState = {
    startClientX: number;
    startClientY: number;
    startBoxX: number;
    startBoxY: number;
  };
  const dragRef = useRef<DragState | null>(null);

  const getCurrentBoxXY = () => {
    const el = calculatorRef.current!;
    const rect = el.getBoundingClientRect();
    if (pos.x === null || pos.y === null) {
      return {
        x: (window.innerWidth - rect.width) / 2,
        y: (window.innerHeight - rect.height) / 2,
      };
    }
    return { x: pos.x, y: pos.y };
  };

  const clampToViewport = (x: number, y: number) => {
    const el = calculatorRef.current;
    if (!el) return { x, y };
    const rect = el.getBoundingClientRect();
    const vw = window.visualViewport?.width ?? window.innerWidth;
    const vh = window.visualViewport?.height ?? window.innerHeight;
    const pad = 8;
    return {
      x: Math.min(Math.max(x, pad), vw - rect.width - pad),
      y: Math.min(Math.max(y, pad), vh - rect.height - pad),
    };
  };

  const centerIfNeeded = () => {
    const el = calculatorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      x: (window.innerWidth - rect.width) / 2,
      y: (window.innerHeight - rect.height) / 2,
    });
  };

  useEffect(() => {
    if (isOpen && (pos.x === null || pos.y === null)) centerIfNeeded();
  }, [isOpen]);

  useEffect(() => {
    const onResize = () => {
      if (pos.x !== null && pos.y !== null) {
        const { x, y } = clampToViewport(pos.x, pos.y);
        setPos({ x, y });
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos.x, pos.y]);

  const onHeaderPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("[data-nodrag]")) return;
    const { x, y } = getCurrentBoxXY();
    dragRef.current = {
      startClientX: e.clientX,
      startClientY: e.clientY,
      startBoxX: x,
      startBoxY: y,
    };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    if (e.pointerType === "touch") e.preventDefault();
  };

  const onHeaderPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const d = dragRef.current;
    const { x, y } = clampToViewport(
      d.startBoxX + (e.clientX - d.startClientX),
      d.startBoxY + (e.clientY - d.startClientY)
    );
    setPos({ x, y });
  };

  const onHeaderPointerUp = (e: React.PointerEvent) => {
    if (dragRef.current) {
      (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
      dragRef.current = null;
    }
  };

  useEffect(() => {
    const el = displayScrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [display]);

  useEffect(() => {
    const el = calcScrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [calculationString, display, waitingForOperand, operation]);

  const inputNumber = useCallback(
    (num: string) => {
      if (waitingForOperand) {
        setDisplay(num);
        setWaitingForOperand(false);
      } else {
        setDisplay(display === "0" ? num : display + num);
      }
    },
    [waitingForOperand, display]
  );

  const inputDecimal = useCallback(() => {
    if (waitingForOperand) {
      setDisplay("0.");
      setWaitingForOperand(false);
    } else if (!display.includes(".")) {
      setDisplay(display + ".");
    }
  }, [waitingForOperand, display]);

  const clear = useCallback(() => {
    setDisplay("0");
    setCalculationString("");
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  }, []);

  const calculate = (a: number, b: number, op: string): number => {
    let result: number;
    switch (op) {
      case "+": result = a + b; break;
      case "-": result = a - b; break;
      case "×": result = a * b; break;
      case "÷": result = b !== 0 ? a / b : 0; break;
      case "%": result = (a * b) / 100; break;
      default:  result = b;
    }
    return Math.round(result * 100000000) / 100000000;
  };

  const performOperation = useCallback(
    (nextOp: string) => {
      const inputValue = parseFloat(display);
      if (previousValue === null) {
        setPreviousValue(inputValue);
        setCalculationString(display + " " + nextOp + " ");
      } else if (operation) {
        const newValue = calculate(previousValue, inputValue, operation);
        setDisplay(String(newValue));
        setPreviousValue(newValue);
        const base = calculationString.includes(" = ")
          ? calculationString.split(" = ")[0]
          : calculationString;
        setCalculationString(base + display + " " + nextOp + " ");
      }
      setWaitingForOperand(true);
      setOperation(nextOp);
    },
    [display, previousValue, operation, calculationString]
  );

  const handleEquals = useCallback(() => {
    const inputValue = parseFloat(display);
    if (previousValue !== null && operation) {
      const newValue = calculate(previousValue, inputValue, operation);
      const base = calculationString.includes(" = ")
        ? calculationString.split(" = ")[0]
        : calculationString;
      setCalculationString(base + display + " = " + newValue);
      setDisplay(String(newValue));
      setPreviousValue(null);
      setOperation(null);
      setWaitingForOperand(true);
      if (onResult) onResult(newValue);
    }
  }, [display, previousValue, operation, calculationString, onResult]);

  const handleBackspace = useCallback(() => {
    if (waitingForOperand) return;
    setDisplay(display.length > 1 ? display.slice(0, -1) : "0");
  }, [display, waitingForOperand]);

  const getCurrentCalculationDisplay = useCallback(() => {
    if (!calculationString) return "";
    if (!waitingForOperand && operation && !calculationString.includes(" = ")) {
      return calculationString + display;
    }
    return calculationString;
  }, [calculationString, display, waitingForOperand, operation]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!isOpen) return;
      e.preventDefault();
      if (e.key >= "0" && e.key <= "9") inputNumber(e.key);
      else if (e.key === ".") inputDecimal();
      else if (e.key === "+") performOperation("+");
      else if (e.key === "-") performOperation("-");
      else if (e.key === "*") performOperation("×");
      else if (e.key === "/") performOperation("÷");
      else if (e.key === "%") performOperation("%");
      else if (e.key === "Enter" || e.key === "=") handleEquals();
      else if (e.key === "Escape") clear();
      else if (e.key === "Backspace") handleBackspace();
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isOpen, clear, handleBackspace, handleEquals, inputDecimal, inputNumber, performOperation]);

  if (!isOpen) return null;

  const numBtnSx = {
    height: "4rem",
    minWidth: 0,
    width: "100%",
    borderRadius: "0.428571429rem",
    backgroundColor: Colors.BACKGROUND_GRAY,
    color: Colors.TEXT_GRAY_DARK,
    fontSize: "1.42857143rem",
    fontWeight: 500,
    textTransform: "none",
    boxShadow: "none",
    border: `0.0714285714rem solid ${Colors.ACCENT_GRAY}`,
    "&:hover": { backgroundColor: Colors.BACKSHADE_GRAY, boxShadow: "none" },
    "&:active": { transform: "scale(0.95)" },
  };

  const opBtnSx = {
    height: "4rem",
    minWidth: 0,
    width: "100%",
    borderRadius: "0.428571429rem",
    backgroundColor: primaryAlpha(0.07),
    color: Colors.PRIMARY,
    fontSize: "1.71428571rem",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "none",
    border: `0.0714285714rem solid ${primaryAlpha(0.2)}`,
    "&:hover": { backgroundColor: primaryAlpha(0.14), boxShadow: "none" },
    "&:active": { transform: "scale(0.95)" },
  };

  return (
    <Portal container={document.body}>
      <Box
        ref={calculatorRef}
        sx={{
          position: "fixed",
          left: pos.x === null ? "50%" : `${pos.x}px`,
          top: pos.y === null ? "50%" : `${pos.y}px`,
          transform: pos.x === null && pos.y === null ? "translate(-50%, -50%)" : "none",
          width: "24.2857143rem",
          backgroundColor: Colors.WHITE,
          borderRadius: "1.14285714rem",
          boxShadow: "0 0.571428571rem 2.28571429rem rgba(0,0,0,0.15)",
          border: `0.0714285714rem solid ${Colors.ACCENT_GRAY}`,
          display: "flex",
          flexDirection: "column",
          zIndex: 9999,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          data-drag-handle
          onPointerDown={onHeaderPointerDown}
          onPointerMove={onHeaderPointerMove}
          onPointerUp={onHeaderPointerUp}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0.857142857rem 1.42857143rem",
            backgroundColor: Colors.PRIMARY,
            cursor: "grab",
            touchAction: "none",
            "&:active": { cursor: "grabbing" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.571428571rem" }}>
            <DragIcon sx={{ fontSize: "1.14285714rem", color: "rgba(255,255,255,0.6)" }} />
            <Typography
              sx={{
                fontSize: "1.14285714rem",
                fontWeight: 600,
                color: Colors.WHITE,
                letterSpacing: "0.02em",
              }}
            >
              Calculator
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            data-nodrag
            sx={{
              color: Colors.WHITE,
              padding: "0.285714286rem",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
            }}
          >
            <CloseIcon sx={{ fontSize: "1.14285714rem" }} />
          </IconButton>
        </Box>

        {/* Display */}
        <Box
          sx={{
            padding: "0.857142857rem 1.14285714rem 0.571428571rem",
            backgroundColor: Colors.BACKSHADE_GRAY,
            borderBottom: `0.0714285714rem solid ${Colors.ACCENT_GRAY}`,
          }}
        >
          <Box
            sx={{
              width: "100%",
              backgroundColor: Colors.WHITE,
              borderRadius: "0.571428571rem",
              border: `0.0714285714rem solid ${Colors.ACCENT_GRAY}`,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: "0.571428571rem 0.857142857rem",
              minHeight: "6rem",
              overflow: "hidden",
            }}
          >
            <Box
              ref={calcScrollRef}
              sx={{
                width: "100%",
                overflowX: "auto",
                whiteSpace: "nowrap",
                "&::-webkit-scrollbar": { height: 0 },
                scrollbarWidth: "none",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.857142857rem",
                  color: Colors.ACCENT_GRAY_DISABLED,
                  fontFamily: "monospace",
                  textAlign: "right",
                  minHeight: "1.14285714rem",
                  lineHeight: "1.14285714rem",
                }}
              >
                {getCurrentCalculationDisplay()}
              </Typography>
            </Box>
            <Box
              ref={displayScrollRef}
              sx={{
                width: "100%",
                overflowX: "auto",
                whiteSpace: "nowrap",
                "&::-webkit-scrollbar": { height: 0 },
                scrollbarWidth: "none",
              }}
            >
              <Typography
                sx={{
                  fontSize:
                    display.length > 10 && display.length < 15
                      ? "2rem"
                      : display.length >= 15 && display.length <= 20
                      ? "1.64285714rem"
                      : display.length > 20
                      ? "1.42857143rem"
                      : "2.57142857rem",
                  fontWeight: 300,
                  color: Colors.TEXT_GRAY_DARK,
                  fontFamily: "monospace",
                  lineHeight: "3.21428571rem",
                  maxHeight: "3.21428571rem",
                  textAlign: "right",
                }}
              >
                {display}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Button Grid */}
        <Box
          sx={{
            padding: "1rem",
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "0.571428571rem",
            backgroundColor: Colors.BACKSHADE_GRAY,
          }}
        >
          {/* Row 1 */}
          <Button
            onClick={clear}
            sx={{
              ...numBtnSx,
              backgroundColor: "#B71C1C",
              color: Colors.WHITE,
              border: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#9B1414", boxShadow: "none" },
            }}
          >
            C
          </Button>
          <Button
            onClick={handleBackspace}
            sx={{
              ...numBtnSx,
              backgroundColor: "#BF8D3A",
              color: Colors.WHITE,
              border: "none",
              fontSize: "1.28571429rem",
              "&:hover": { backgroundColor: "#A67C00", boxShadow: "none" },
            }}
          >
            ⌫
          </Button>
          <Button onClick={() => performOperation("%")} sx={opBtnSx}>%</Button>
          <Button onClick={() => performOperation("÷")} sx={opBtnSx}>÷</Button>

          {/* Row 2 */}
          <Button onClick={() => inputNumber("7")} sx={numBtnSx}>7</Button>
          <Button onClick={() => inputNumber("8")} sx={numBtnSx}>8</Button>
          <Button onClick={() => inputNumber("9")} sx={numBtnSx}>9</Button>
          <Button onClick={() => performOperation("×")} sx={opBtnSx}>×</Button>

          {/* Row 3 */}
          <Button onClick={() => inputNumber("4")} sx={numBtnSx}>4</Button>
          <Button onClick={() => inputNumber("5")} sx={numBtnSx}>5</Button>
          <Button onClick={() => inputNumber("6")} sx={numBtnSx}>6</Button>
          <Button onClick={() => performOperation("-")} sx={opBtnSx}>−</Button>

          {/* Row 4 */}
          <Button onClick={() => inputNumber("1")} sx={numBtnSx}>1</Button>
          <Button onClick={() => inputNumber("2")} sx={numBtnSx}>2</Button>
          <Button onClick={() => inputNumber("3")} sx={numBtnSx}>3</Button>
          <Button onClick={() => performOperation("+")} sx={opBtnSx}>+</Button>

          {/* Row 5 */}
          <Button onClick={() => inputNumber("0")} sx={{ ...numBtnSx, gridColumn: "span 2" }}>
            0
          </Button>
          <Button onClick={inputDecimal} sx={{ ...numBtnSx, fontSize: "1.71428571rem", fontWeight: 600 }}>
            .
          </Button>
          <Button
            onClick={handleEquals}
            sx={{
              height: "4rem",
              minWidth: 0,
              width: "100%",
              borderRadius: "0.428571429rem",
              backgroundColor: Colors.PRIMARY,
              color: Colors.WHITE,
              fontSize: "1.71428571rem",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              border: "none",
              "&:hover": { backgroundColor: Colors.PRIMARY_DARK, boxShadow: "none" },
              "&:active": { transform: "scale(0.95)" },
            }}
          >
            =
          </Button>
        </Box>
      </Box>
    </Portal>
  );
};

export default Calculator;
